import React, { useEffect, useState } from 'react';
import { Modal, Table, InputNumber, Typography, Tag, Space, Alert, Button } from 'antd';

const { Text } = Typography;

export interface ReserveAvailableStock {
  stock_id: number;
  stock_code?: string;
  udc_pallet?: string;
  udc_collo?: string;
  warehouse?: string;
  document_number?: string;
  entry_date?: string;
  qta_available: number;
  reserved_by_others: number;
  free: number;
}

export interface ReserveAvailableProduct {
  product_id: number;
  internal_product_code?: string;
  size?: string;
  color?: string;
  client_product_code?: string;
  product_description?: string;
  requested: number;
  already_reserved: number;
  missing: number;
  unit_price: number;
  total_free: number;
  to_reserve: number;
  needs_choice: boolean;
  proposal: Record<string, number>;
  stocks: ReserveAvailableStock[];
  skipped_reason?: string | null;
}

interface ReserveAvailableChoiceModalProps {
  visible: boolean;
  products: ReserveAvailableProduct[];   // solo i prodotti in eccedenza
  autoProducts: number;                   // prodotti opzionati senza scelta
  autoQuantity: number;                   // pezzi opzionati senza scelta
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (allocations: Record<number, Record<number, number>>) => void;
}

export const productLabel = (p: ReserveAvailableProduct) => {
  const code = p.internal_product_code || `ID ${p.product_id}`;
  if (!p.size && !p.color) return code;
  if (p.size && !p.color) return `${code}-${p.size}`;
  if (!p.size && p.color) return `${code}--${p.color}`;
  return `${code}-${p.size}-${p.color}`;
};

const ReserveAvailableChoiceModal: React.FC<ReserveAvailableChoiceModalProps> = ({
  visible,
  products,
  autoProducts,
  autoQuantity,
  submitting,
  onCancel,
  onConfirm
}) => {
  // quantità scelte: product_id -> stock_id -> pezzi (precompilate dalla proposta FIFO)
  const [quantities, setQuantities] = useState<Record<number, Record<number, number>>>({});

  useEffect(() => {
    if (!visible) return;
    const initial: Record<number, Record<number, number>> = {};
    products.forEach(p => {
      initial[p.product_id] = {};
      p.stocks.forEach(s => {
        initial[p.product_id][s.stock_id] = p.proposal[String(s.stock_id)] || 0;
      });
    });
    setQuantities(initial);
  }, [visible, products]);

  const assigned = (productId: number) =>
    Object.values(quantities[productId] || {}).reduce((sum, q) => sum + (q || 0), 0);

  const hasExcess = products.some(p => assigned(p.product_id) > p.missing);

  const setQty = (productId: number, stockId: number, value: number | null) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [stockId]: value && value > 0 ? Math.floor(value) : 0 }
    }));
  };

  const fillOldest = (p: ReserveAvailableProduct) => {
    const next: Record<number, number> = {};
    p.stocks.forEach(s => { next[s.stock_id] = p.proposal[String(s.stock_id)] || 0; });
    setQuantities(prev => ({ ...prev, [p.product_id]: next }));
  };

  const clearProduct = (p: ReserveAvailableProduct) => {
    const next: Record<number, number> = {};
    p.stocks.forEach(s => { next[s.stock_id] = 0; });
    setQuantities(prev => ({ ...prev, [p.product_id]: next }));
  };

  const handleConfirm = () => {
    const allocations: Record<number, Record<number, number>> = {};
    products.forEach(p => {
      allocations[p.product_id] = {};
      Object.entries(quantities[p.product_id] || {}).forEach(([stockId, qty]) => {
        if (qty > 0) allocations[p.product_id][Number(stockId)] = qty;
      });
    });
    onConfirm(allocations);
  };

  const columnsFor = (p: ReserveAvailableProduct) => [
    {
      title: 'Data ingresso',
      dataIndex: 'entry_date',
      key: 'entry_date',
      width: 110,
      render: (v?: string) => v ? new Date(v).toLocaleDateString('it-IT') : '-',
    },
    {
      title: 'UDC',
      key: 'udc',
      width: 180,
      render: (_: any, s: ReserveAvailableStock) => (!s.udc_pallet && !s.udc_collo) ? '-' : (
        <Space direction="vertical" size={0}>
          {s.udc_pallet && <span>Pallet: {s.udc_pallet}</span>}
          {s.udc_collo && <span>Collo: {s.udc_collo}</span>}
        </Space>
      ),
    },
    { title: 'Magazzino', dataIndex: 'warehouse', key: 'warehouse', width: 150, render: (v?: string) => v || '-' },
    { title: 'Stock Code', dataIndex: 'stock_code', key: 'stock_code', width: 130, render: (v?: string) => v || '-' },
    { title: 'Doc. carico', dataIndex: 'document_number', key: 'document_number', width: 170, render: (v?: string) => v || '-' },
    {
      title: 'Libero',
      key: 'free',
      width: 120,
      align: 'right' as const,
      render: (_: any, s: ReserveAvailableStock) => (
        <span title={`Disponibile ${s.qta_available}, già prenotati da altri ordini ${s.reserved_by_others}`}>
          <Tag color="green">{s.free}</Tag>
        </span>
      ),
    },
    {
      title: 'Da opzionare',
      key: 'qty',
      width: 130,
      align: 'right' as const,
      render: (_: any, s: ReserveAvailableStock) => (
        <InputNumber
          min={0}
          max={s.free}
          precision={0}
          size="small"
          value={quantities[p.product_id]?.[s.stock_id] ?? 0}
          onChange={(v) => setQty(p.product_id, s.stock_id, v as number | null)}
          style={{ width: 100 }}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Scegli da quali stock prendere i prodotti"
      open={visible}
      onCancel={onCancel}
      width={1100}
      okText="Opziona"
      cancelText="Annulla"
      onOk={handleConfirm}
      okButtonProps={{ disabled: hasExcess, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={false}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Le quantità sono già proposte dagli stock più vecchi: modificale se vuoi prendere i pezzi da altri stock."
        description={autoProducts > 0
          ? `Gli altri ${autoProducts} prodotti (${autoQuantity} pz) hanno solo il disponibile necessario e verranno opzionati automaticamente.`
          : undefined}
      />
      {products.map(p => {
        const tot = assigned(p.product_id);
        const over = tot > p.missing;
        return (
          <div key={p.product_id} style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
              <div>
                <Text strong>{productLabel(p)}</Text>
                {p.client_product_code && <Text type="secondary"> · {p.client_product_code}</Text>}
                <div><Text type="secondary" style={{ fontSize: 12 }}>{p.product_description}</Text></div>
              </div>
              <Space>
                <Text>Richiesto {p.requested} · già prenotato {p.already_reserved}</Text>
                <Tag color={over ? 'red' : tot === p.missing ? 'green' : 'orange'}>
                  Assegnati {tot} / da opzionare {p.missing}
                </Tag>
                <Button size="small" onClick={() => fillOldest(p)}>Più vecchi</Button>
                <Button size="small" onClick={() => clearProduct(p)}>Azzera</Button>
              </Space>
            </Space>
            <Table
              size="small"
              rowKey="stock_id"
              columns={columnsFor(p)}
              dataSource={p.stocks}
              pagination={false}
              scroll={{ y: 300 }}
            />
            {over && (
              <Text type="danger" style={{ fontSize: 12 }}>
                Hai assegnato {tot - p.missing} pz in più di quanti ne mancano all'ordine.
              </Text>
            )}
          </div>
        );
      })}
    </Modal>
  );
};

export default ReserveAvailableChoiceModal;
